import { Capacitor } from '@capacitor/core';
import { debugLog, debugError } from '../utils/debugUtils';

const SOUND_KEY = 'concordare_sound_enabled';
const VIBRATION_KEY = 'concordare_vibration_enabled';

class SoundVibrationService {
  constructor() {
    this.soundEnabled = localStorage.getItem(SOUND_KEY) !== 'false';
    this.vibrationEnabled = localStorage.getItem(VIBRATION_KEY) !== 'false';
    this.audioContext = null;
  }

  isSoundEnabled() {
    return this.soundEnabled;
  }

  isVibrationEnabled() {
    return this.vibrationEnabled;
  }

  setSoundEnabled(enabled) {
    this.soundEnabled = enabled;
    localStorage.setItem(SOUND_KEY, String(enabled));
  }

  setVibrationEnabled(enabled) {
    this.vibrationEnabled = enabled;
    localStorage.setItem(VIBRATION_KEY, String(enabled));
  }

  _getAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this.audioContext;
  }

  playTone(frequency = 440, duration = 0.1, type = 'sine') {
    if (!this.soundEnabled) return;
    try {
      const ctx = this._getAudioContext();
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      oscillator.type = type;
      oscillator.frequency.value = frequency;
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (err) {
      debugError('[SoundVibrationService] playTone error:', err);
    }
  }

  playSuccess() {
    this.playTone(523, 0.15, 'sine'); // C5
  }

  playError() {
    this.playTone(220, 0.2, 'square');
  }

  async vibrate(duration = 50) {
    if (!this.vibrationEnabled) return;
    try {
      if (Capacitor.isNativePlatform()) {
        const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
        await Haptics.impact({ style: ImpactStyle.Light });
      } else if (navigator.vibrate) {
        navigator.vibrate(duration);
      }
    } catch (err) {
      debugError('[SoundVibrationService] vibrate error:', err);
    }
  }

  async vibrateSuccess() {
    this.playSuccess();
    await this.vibrate(50);
  }

  async vibrateError() {
    this.playError();
    await this.vibrate(100);
  }

  debugLog() {
    debugLog('[SoundVibrationService] sound:', this.soundEnabled, 'vibration:', this.vibrationEnabled);
  }
}

export default new SoundVibrationService();
